import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, Plus, Trash2, ArrowRight, RefreshCcw, Lock, Link2, Layers, X, Eye, EyeOff, ExternalLink } from "lucide-react";
import { projectsApi, materialsApi } from "@/lib/api";
import { grandTotal, fmtMoney, PROJECT_CATEGORIES, WOOD_SYSTEM_TYPES, DEFAULT_WH } from "@/lib/calc";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const HERO = "https://customer-assets-v7afamib.emergentagent.net/job_timber-calc-14/artifacts/f2m936jb_Resim1n.jpg";

export default function ProjectList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [projects, setProjects] = useState([]);
  const [materialsById, setMaterialsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "structural", system_type: "clt", prepared_by: "", project_date: new Date().toISOString().slice(0, 10) });
  const [deleteId, setDeleteId] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [variantFilter, setVariantFilter] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [groupDialog, setGroupDialog] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [kdv, setKdv] = useState(20);

  const load = async () => {
    setLoading(true);
    try {
      const [proj, mats] = await Promise.all([projectsApi.list(), materialsApi.list()]);
      setProjects(proj);
      setMaterialsById(Object.fromEntries(mats.map((m) => [m.id, m])));
    } catch (e) {
      toast.error("Projeler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Proje adı gerekli");
    try {
      const isWH = form.category === "wooden_house";
      const p = await projectsApi.create({
        name: form.name.trim(),
        category: form.category,
        system_type: isWH ? form.system_type : null,
        wooden_house: isWH ? DEFAULT_WH : null,
        prepared_by: form.prepared_by,
        project_date: form.project_date,
        price_mode: "live",
        line_items: [],
      });
      toast.success("Proje oluşturuldu");
      nav(`/projects/${p.id}`);
    } catch (e) {
      toast.error("Oluşturulamadı");
    }
  };

  const confirmDelete = async () => {
    try {
      await projectsApi.remove(deleteId);
      toast.success("Proje silindi");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const toggleVisibility = async (p) => {
    const next = p.visible_to_users === false; // hidden -> make visible
    try {
      await projectsApi.setVisibility(p.id, next);
      setProjects((prev) => prev.map((x) => x.id === p.id ? { ...x, visible_to_users: next } : x));
      toast.success(next ? "Proje herkese görünür yapıldı" : "Proje gizlendi (sadece yönetici)");
    } catch (e) {
      toast.error("Görünürlük değiştirilemedi");
    }
  };

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const exitSelect = () => { setSelectMode(false); setSelected([]); };
  const openGroupDialog = () => {
    if (selected.length < 2) return toast.error("Gruplamak için en az 2 proje seçin");
    setGroupName("");
    setGroupDialog(true);
  };
  const confirmGroup = async () => {
    if (!groupName.trim()) return toast.error("Grup adı gerekli");
    try {
      await projectsApi.group(selected, null, groupName.trim());
      toast.success(`"${groupName.trim()}" grubu oluşturuldu`);
      setGroupDialog(false);
      exitSelect();
      load();
    } catch (e) {
      toast.error("Gruplama başarısız");
    }
  };

  const variantCounts = {};
  projects.forEach((p) => { if (p.variant_group) variantCounts[p.variant_group] = (variantCounts[p.variant_group] || 0) + 1; });
  const filtered = projects.filter((p) => (catFilter === "all" || (p.category || "structural") === catFilter) && (!variantFilter || p.variant_group === variantFilter));

  const onRowClick = (p) => { if (selectMode) toggleSelect(p.id); else nav(`/projects/${p.id}`); };
  const activeGroupName = variantFilter ? (projects.find((p) => p.variant_group === variantFilter && p.group_name) || {}).group_name || "Grup" : "";

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 md:p-8 space-y-6">
      <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-900">
        <img src={HERO} alt="Timber structure" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative p-8 md:p-10">
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-white">Projeler & Teklifler</h1>
          <p className="text-slate-200 mt-2 max-w-xl text-sm md:text-base">
            Glulam, CLT ve karkas işleri için canlı maliyet toplamlarıyla teklif hazırlayın.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={() => setOpen(true)} data-testid="new-project-btn"
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 transition-colors">
              <Plus className="w-4 h-4" /> Yeni Proje
            </button>
            <button onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} data-testid="bulk-group-btn"
              className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors border ${selectMode ? "bg-white text-slate-800 border-white" : "bg-transparent text-white border-white/40 hover:bg-white/10"}`}>
              <Layers className="w-4 h-4" /> {selectMode ? "Seçimi iptal et" : "Toplu proje oluştur"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="category-filter">
        {[["all", "Tümü"], ...Object.entries(PROJECT_CATEGORIES)].map(([key, label]) => (
          <button key={key} onClick={() => setCatFilter(key)} data-testid={`filter-${key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
              catFilter === key
                ? "bg-amber-600 text-white border-amber-600"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {selectMode && (
        <div className="flex flex-wrap items-center gap-3 rounded-md bg-amber-50 border border-amber-200 px-4 py-3" data-testid="select-bar">
          <span className="text-sm font-medium text-amber-900">Bir teklifte birleştirmek istediğiniz projeleri seçin. Seçili: {selected.length}</span>
          <button onClick={openGroupDialog} data-testid="group-selected-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-40"
            disabled={selected.length < 2}>
            <Layers className="w-4 h-4" /> Seçilenleri grupla
          </button>
          <button onClick={exitSelect} className="inline-flex items-center gap-1 text-sm font-medium text-amber-800 hover:underline">
            <X className="w-4 h-4" /> Vazgeç
          </button>
        </div>
      )}

      {variantFilter && (
        <div className="flex flex-wrap items-center gap-3 rounded-md bg-indigo-50 border border-indigo-200 px-4 py-2" data-testid="variant-filter-bar">
          <span className="text-sm text-indigo-800"><b data-testid="active-group-name">{activeGroupName}</b> grubu gösteriliyor.</span>
          <span className="inline-flex items-center gap-1 text-sm text-indigo-800">KDV
            <input type="number" step="1" min="0" value={kdv} data-testid="group-kdv-input"
              onChange={(e) => setKdv(e.target.value === "" ? 0 : parseFloat(e.target.value))}
              className="w-16 rounded border border-indigo-200 px-2 py-1 text-right tabular-nums outline-none focus:ring-2 focus:ring-indigo-500" />%
          </span>
          <button onClick={() => window.open(projectsApi.groupExportUrl(variantFilter, kdv, "cost"), "_blank")} data-testid="group-cost-export-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100">
            <FileSpreadsheet className="w-4 h-4" /> Grup maliyet tablosu
          </button>
          <button onClick={() => window.open("https://naswoodteklif.figma.site", "_blank")} data-testid="open-teklif-app-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700">
            <ExternalLink className="w-4 h-4" /> Teklif Hazırla
          </button>
          <button onClick={() => setVariantFilter(null)} data-testid="clear-variant-filter" className="text-sm font-semibold text-indigo-700 hover:underline">Tümünü göster</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-100">
              {selectMode && <th className="px-4 py-3 border-b border-slate-200 w-10"></th>}
              {["Proje", "Kategori", "Hazırlayan", "Tarih", "Fiyatlama", "Genel Toplam", ""].map((h, i) => (
                <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 ${i === 5 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={selectMode ? 8 : 7} className="px-4 py-10 text-center text-slate-400">Yükleniyor…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={selectMode ? 8 : 7} className="px-4 py-10 text-center text-slate-400" data-testid="projects-empty">Bu kategoride henüz proje yok. İlk teklifinizi oluşturun.</td></tr>
            ) : filtered.map((p) => (
              <tr key={p.id} className={`transition-colors cursor-pointer ${selected.includes(p.id) ? "bg-amber-50" : p.visible_to_users === false ? "bg-slate-50/70" : "hover:bg-slate-50"}`} data-testid={`project-row-${p.id}`}
                onClick={() => onRowClick(p)}>
                {selectMode && (
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(p.id)} data-testid={`select-project-${p.id}`}
                      onChange={() => toggleSelect(p.id)} className="w-4 h-4 accent-amber-600" />
                  </td>
                )}
                <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                  {p.name}
                  {p.visible_to_users === false && (
                    <span data-testid={`hidden-badge-${p.id}`} title="Sadece yöneticiler görür"
                      className="ml-2 inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                      <EyeOff className="w-3 h-3" /> Gizli
                    </span>
                  )}
                  {p.variant_group && variantCounts[p.variant_group] > 1 && (
                    <button onClick={(e) => { e.stopPropagation(); setVariantFilter(variantFilter === p.variant_group ? null : p.variant_group); }}
                      data-testid={`variant-chip-${p.id}`}
                      className="ml-2 inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-200">
                      <Link2 className="w-3 h-3" /> {p.group_name || `${variantCounts[p.variant_group]} proje grubu`}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    (p.category || "structural") === "wooden_house"
                      ? "bg-orange-100 text-orange-800"
                      : "bg-sky-100 text-sky-800"
                  }`} data-testid={`project-category-${p.id}`}>
                    {PROJECT_CATEGORIES[p.category] || PROJECT_CATEGORIES.structural}
                    {p.category === "wooden_house" && p.system_type ? ` · ${WOOD_SYSTEM_TYPES[p.system_type] || ""}` : ""}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{p.prepared_by || "—"}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{p.project_date}</td>
                <td className="px-4 py-3">
                  {p.price_mode === "live" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800"><RefreshCcw className="w-3 h-3" /> Canlı</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"><Lock className="w-3 h-3" /> Kilitli</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-slate-900 tabular-nums text-right">
                  {fmtMoney(grandTotal(p.line_items || [], materialsById, p.price_mode))}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && (
                      <button onClick={(e) => { e.stopPropagation(); toggleVisibility(p); }} data-testid={`toggle-visibility-${p.id}`}
                        className={`p-2 rounded-md hover:bg-slate-100 ${p.visible_to_users === false ? "text-slate-400" : "text-emerald-600"}`}
                        title={p.visible_to_users === false ? "Gizli — görünür yap" : "Görünür — gizle"}>
                        {p.visible_to_users === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); nav(`/projects/${p.id}`); }} data-testid={`open-project-${p.id}`}
                      className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900" title="Aç"><ArrowRight className="w-4 h-4" /></button>
                    <button onClick={(e) => { e.stopPropagation(); setDeleteId(p.id); }} data-testid={`delete-project-${p.id}`}
                      className="p-2 rounded-md text-red-500 hover:bg-red-50" title="Sil"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
        <DialogContent className="sm:max-w-md" data-testid="group-name-dialog">
          <DialogHeader><DialogTitle className="font-heading">Grubu adlandır</DialogTitle>
            <DialogDescription>Seçilen projeleri ortak bir teklif adı altında birleştirin.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label>Grup / Teklif adı</Label>
            <Input data-testid="group-name-input" value={groupName} autoFocus
              onChange={(e) => setGroupName(e.target.value)} placeholder="ör. Villa A — Teklif 1"
              onKeyDown={(e) => { if (e.key === "Enter") confirmGroup(); }} />
            <p className="text-xs text-slate-400">{selected.length} proje bu grup altında birleştirilecek.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setGroupDialog(false)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
            <button onClick={confirmGroup} data-testid="confirm-group-btn" className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700">Grupla</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-project-dialog">
          <DialogHeader><DialogTitle className="font-heading">Yeni Proje</DialogTitle>
            <DialogDescription>Kategori seçip yeni bir teklif/proje oluşturun.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Proje adı</Label>
              <Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ör. Depo çatı strüktürü" />
            </div>
            <div className="grid gap-2">
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="project-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PROJECT_CATEGORIES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.category === "wooden_house" && (
              <div className="grid gap-2">
                <Label>Ahşap ev sistemi</Label>
                <Select value={form.system_type} onValueChange={(v) => setForm({ ...form, system_type: v })}>
                  <SelectTrigger data-testid="project-systemtype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(WOOD_SYSTEM_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Hazırlayan</Label>
                <Input data-testid="project-preparedby-input" value={form.prepared_by} onChange={(e) => setForm({ ...form, prepared_by: e.target.value })} placeholder="İsim" />
              </div>
              <div className="grid gap-2">
                <Label>Tarih</Label>
                <Input data-testid="project-date-input" type="date" value={form.project_date} onChange={(e) => setForm({ ...form, project_date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
            <button onClick={create} data-testid="create-project-btn" className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700">Oluştur</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Proje silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem teklifi ve tüm kalemlerini kalıcı olarak siler.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-project" className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
